import { useMemo } from 'react'
import type { PresentationContext } from '@commerce/core'
import { useStorefrontConfig } from '../config/StorefrontConfigContext'
import { useAppSelector } from './useAppSelector'

/**
 * Everything a card needs to know about the storefront it is being drawn for,
 * assembled from the two places that know it.
 *
 * `commerce` is what the storefront authored. `hidePricesActive` is what the
 * session derived for *this* customer, and it only exists once core's config
 * slice has both the rule and the session data.
 *
 * The two hide-prices switches are independent and both are real:
 * `commerce.hidePrices` turns prices off for the whole storefront, and
 * `hidePricesActive` turns them off for one customer. Reading only one would
 * show prices to a customer who must not see them — so they are OR'd, not
 * chosen between.
 */
export const usePresentationContext = (): PresentationContext => {
  const { commerce } = useStorefrontConfig()
  const hidePricesForCustomer = useAppSelector(
    (state) => state.config.config?.options?.hidePricesActive === true,
  )

  return useMemo(
    () => ({
      currencySymbol: commerce.currencySymbol,
      locale: commerce.locale,
      hidePrices: commerce.hidePrices || hidePricesForCustomer,
      placeholderImage: commerce.placeholderImage,
      priceFormat: commerce.priceFormat,
      currencyCode: commerce.currencyCode,
      priceDisplay: commerce.priceDisplay,
    }),
    [commerce, hidePricesForCustomer],
  )
}
